import React, { useContext } from 'react'
import { Box, Button, Card, Container, Grid, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment/moment'
import clsx from 'clsx'

import { getAgeFromDateOfBirth, isPassedDate } from '../../../helpers/utils/membership'
import { MembershipCardContentSkeleton } from './MembershipCardContent.skeleton'
import { MembershipStatus } from '../../../types/MembershipStatus'
import { AppContext } from '../../../stores/App/AppStore'
import { Membership } from '../../../types'
import { maxMemberAge } from '../../../helpers/validation/age'
import normalCardFace from '../../../assets/new-card-face.png'
import specimenCardFace from '../../../assets/new-specimen-card-face.png'
import { UserContext } from '../../../stores/User/UserStore'

import styles from './MembershipCardContent.module.css'
/* import { formatWithThousandCommas } from '../../../helpers/utils/common'
import GtmEventName from '../../../helpers/analytics/GtmEventName'
import useGoogleTagManager from '../../../helpers/analytics/use-google-tag-manager'
import { Route } from '../../../router'
import { useNavigate } from 'react-router-dom'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward' */

interface MembershipCardContentProps {
    membership?: Membership
}

const MembershipCardContent: React.FC<MembershipCardContentProps> = (props: MembershipCardContentProps) => {
    const { membership } = props

    const { state } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)
    const { t } = useTranslation()
    /* const { trackButtonClick } = useGoogleTagManager()
    const navigate = useNavigate() */

    const hasMembership = !!membership?.id
    const isProduction = process.env.REACT_APP_STARTER_APP_ENV === `production`

    if (!hasMembership || !membership.dateOfBirth) {
        return <MembershipCardContentSkeleton />
    }

    const membershipAgeCheck = (membership: Membership): boolean => {
        if (membership.user?.liftAgeLimit) {
            return false
        }

        return maxMemberAge ? moment().diff(membership.dateOfBirth, `years`) >= maxMemberAge : false
    }

    const handleOtherNTUCMembershipClick = () => {
        window.open(state.pages.membershipCard.otherMembershipUrl, `_self`)
    }

    /* const handleLinkpointClick = () => {
        trackButtonClick(GtmEventName.LINKPOINT_BALANCE_PROFILE_CLICK)
        navigate(`${Route.LINKPOINT_GUIDE}`)
    } */

    const strapiMembershipCardFace = userState.membershipSettings?.data?.bannerImageURL

    const defaultCardFace = isProduction ? normalCardFace : specimenCardFace
    const cardFace = strapiMembershipCardFace ?? defaultCardFace

    const idDOBString = membership.id + `-` + moment(membership.dateOfBirth, `YYYY-MM-DD`).format(`DDMMYYYY`)
    const signUpDateString = moment(membership.signUpDate, `YYYY-MM-DD`).format(`DD MMMM YYYY`)

    //const linkpointBalance = userState.linkpointPool?.balance ? formatWithThousandCommas(Number(userState.linkpointPool?.balance)) + ` ($${userState.linkpointPool.dollarValue})` : `NA`

    return (
        <Card className={styles.cardBody}>
            {(membership.status === MembershipStatus.ACTIVE || membership.status === MembershipStatus.ADMIN) ?
                <ActiveMembershipHeader /> :
                <SuspendedMembershipHeader />
            }
            <Container className={styles.unionCardContainer}>
                <Container component={`img`} src={cardFace} className={styles.unionCard} />
                <Typography variant={`body1`} className={styles.unionCardIdDOB}>{idDOBString}</Typography>
                <Typography variant={`body1`} className={styles.unionCardName}>{membership.eCardName}</Typography>
            </Container>

            <Grid container direction="row" justifyContent="space-between" className={styles.unionDetails}>
                <Stack>
                    <Typography variant={`body2`} className={styles.unionDetailsHeader}>
                        {t(`membership_expiry`)}
                    </Typography>

                    {isPassedDate(membership.expiryDate?.toString()) ? (
                        <Typography
                            variant={`subtitle1`}
                            className={clsx(styles.unionDetailsValue, styles.warning)}>{t(`expired`)}</Typography>
                    ) : (
                        <Typography
                            variant={`body1`}
                            className={styles.unionDetailsValue}>{membership.expiryDate?.toString()}</Typography>
                    )}

                    <Typography variant={`body2`} className={styles.membershipPlansHeader}>
                        {t(`membership_plans`)}
                    </Typography>
                    {membership.planUsedOnLastActivation ? (
                        <Typography variant={`body1`}
                            className={styles.unionDetailsValue}>{membership.planUsedOnLastActivation?.name}</Typography>
                    ) : (
                        <Typography variant={`body1`}
                            className={styles.unionDetailsValue}>{t(`regular`)}</Typography>
                    )}

                    <Typography variant={`body2`} className={styles.membershipPlansHeader}>
                        {t(`sign_up_date`)}
                    </Typography>
                    <Typography variant={`body1`}
                        className={styles.unionDetailsValue}>{signUpDateString}</Typography>
                </Stack>
                <Stack>
                    <Typography variant={`body2`} className={styles.unionDetailsHeader}>{t(`age`)}</Typography>
                    <Typography
                        variant={`body1`}
                        className={styles.unionDetailsValue}>{getAgeFromDateOfBirth(membership.dateOfBirth.toString())}</Typography>
                    {/* <Typography variant={`body2`} className={styles.unionDetailsHeader}>{t(`linkpointBalance`)}</Typography>
                    <Grid container direction="row" alignItems="left" spacing={1} marginLeft={`0`}>
                        <Grid display={`flex`} direction={`row`}>
                            <Typography className={styles.linkpointsBalanceTitle} variant={`body2`} onClick={handleLinkpointClick}>
                                <svg height={`25px`} fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.118 15.343V16.5H6.2V8h1.383v7.343h3.535ZM14.955 12.333c.307 0 .577-.04.81-.118a1.59 1.59 0 0 0 .585-.331 1.38 1.38 0 0 0 .354-.525c.08-.205.119-.433.119-.685 0-.248-.04-.47-.119-.667a1.269 1.269 0 0 0-.342-.502 1.512 1.512 0 0 0-.585-.313 2.692 2.692 0 0 0-.822-.112H13.69v3.253h1.265Zm0-4.333c.556 0 1.036.065 1.442.195.41.13.747.313 1.01.549.269.232.468.513.598.844.13.326.195.689.195 1.086 0 .405-.069.777-.207 1.116a2.362 2.362 0 0 1-.615.867c-.271.24-.61.43-1.016.567-.402.134-.871.2-1.407.2H13.69V16.5h-1.383V8h2.648Z" fill="#fff"></path><path d="M22 11.996c0 5.524-4.48 9.997-9.996 9.997C6.48 22 2 17.52 2 11.997 2 6.48 6.48 2 11.996 2 17.52 2 22 6.48 22 11.996Z" fill="#FDA100"></path><path d="M12.102 14.804c.043 1.023-.62 1.708-1.777 1.8h-4.05V7.791H8.52v7.012h3.583ZM12.68 7.975c.607-.098 1.468-.183 2.667-.183 1.214 0 2.089.233 2.674.705.557.438.924 1.164.917 2.025-.007.854-.289 1.587-.811 2.067-.677.635-1.686.924-2.843.924-.261 0-.494-.014-.677-.042l-.014 3.132H12.63l.05-8.628Zm1.934 3.965c.162.035.367.05.656.05 1.05 0 1.7-.53 1.707-1.419.007-.804-.543-1.284-1.538-1.284-.388 0-.663.036-.804.064l-.021 2.589Z" fill="#fff"></path></svg>
                            </Typography>
                            <Typography className={styles.linkpointsBalance} variant={`body2`} onClick={handleLinkpointClick}>
                                {linkpointBalance}
                            </Typography>
                        </Grid>
                        <Grid display='flex' className={styles.iconGridItem} alignItems="center" item>
                            <ArrowOutwardIcon fontSize="medium" className={styles.arrowIcon} onClick={handleLinkpointClick} />
                        </Grid>
                    </Grid> */}
                </Stack>
            </Grid>

            {membershipAgeCheck(membership) &&
                <Box className={styles.otherMembershipButton}>
                    <Button variant='contained' onClick={handleOtherNTUCMembershipClick}>{t(`other_ntuc_memberships`)}</Button>
                </Box>
            }
        </Card>
    )
}

const ActiveMembershipHeader = () => {
    const { t } = useTranslation()

    return (
        <>
            <Typography variant={`h6`} className={styles.unionTitle}>{t(`membership_card_title`)}</Typography>
            <Typography variant={`body1`} component={`span`} className={styles.unionSubtitle}>
                {t(`membership_card_subtitle_1`)}
                <div className={styles.unionSubtitleHighlight}> {t(`membership_card_subtitle_2`)} </div>
                {t(`membership_card_subtitle_3`)}
            </Typography>
        </>
    )
}

const SuspendedMembershipHeader = () => {
    const { t } = useTranslation()

    return (
        <>
            <Typography variant={`h6`} className={styles.unionTitle}>{t(`membership_suspended_title`)}</Typography>
            <Typography variant={`body1`} component={`span`} className={clsx(styles.unionSubtitle, styles.warning)}>
                {t(`membership_suspended_description`)}
            </Typography>
        </>
    )
}

export default MembershipCardContent
export { MembershipCardContentSkeleton } from './MembershipCardContent.skeleton'